/**
 * Resolve declarative bindings against a slot → dataset snapshot map.
 * Empty snapshots → empty values; failed → undefined + meta error (caller decides UI).
 */

function getPath(data, path) {
  if (path == null || path === '' || path === '.') return { ok: true, value: data };
  let cur = data;
  const parts = String(path).split('.');
  for (let i = 0; i < parts.length; i += 1) {
    if (cur != null && typeof cur === 'object' && parts[i] in cur) {
      cur = cur[parts[i]];
    } else {
      return { ok: false, value: undefined };
    }
  }
  return { ok: true, value: cur };
}

export function buildDatasetMap(manifest, datasetsById) {
  /** @type {Record<string, { status: string, data: any }>} */
  const bySlot = {};
  const entries = (manifest && manifest.entries) || [];
  entries.forEach((entry) => {
    if (!entry || !entry.slot) return;
    const snap =
      (datasetsById && (datasetsById[entry.dataset_id] || datasetsById[`${entry.dataset_id}@${entry.dataset_version}`])) ||
      null;
    bySlot[entry.slot] = {
      status: (snap && snap.status) || entry.status || 'validated',
      data: snap && 'data' in snap ? snap.data : snap || {},
    };
  });
  return bySlot;
}

export function resolveBindings(bindings, datasetMap, pageContext = {}) {
  const props = {};
  const meta = { errors: [], emptySlots: [], failedSlots: [] };
  if (!bindings || typeof bindings !== 'object') {
    return { props, meta };
  }
  Object.keys(bindings).forEach((prop) => {
    const binding = bindings[prop];
    if (!binding || typeof binding !== 'object') return;
    const slot = binding.dataset || binding.slot;
    const path = binding.path != null ? binding.path : '';
    const snap = datasetMap && datasetMap[slot];
    if (!snap) {
      meta.errors.push({ prop, slot, reason: 'missing_slot' });
      props[prop] = binding.fallback !== undefined ? binding.fallback : undefined;
      return;
    }
    if (snap.status === 'failed') {
      meta.failedSlots.push(slot);
      meta.errors.push({ prop, slot, reason: 'failed' });
      props[prop] = binding.fallback !== undefined ? binding.fallback : undefined;
      return;
    }
    if (snap.status === 'empty') {
      meta.emptySlots.push(slot);
      props[prop] = binding.fallback !== undefined ? binding.fallback : (Array.isArray(getPath(snap.data, path).value) ? [] : '');
      return;
    }
    const { ok, value } = getPath(snap.data, path);
    if (!ok) {
      meta.errors.push({ prop, slot, path, reason: 'missing_path' });
      props[prop] = binding.fallback !== undefined ? binding.fallback : undefined;
    } else {
      props[prop] = value;
    }
  });

  // Simple {{page.title}} style templates in string props are applied by caller via applyTemplates
  void pageContext;
  return { props, meta };
}

export function applyTemplates(value, ctx) {
  if (typeof value !== 'string') return value;
  return value.replace(/\{\{\s*page\.([a-zA-Z0-9_]+)\s*\}\}/g, (_, key) => {
    if (ctx && ctx.page && ctx.page[key] != null) return String(ctx.page[key]);
    return '';
  });
}

export function mergeNodeProps(node, datasetMap, pageContext) {
  const literals = { ...(node.props || {}) };
  Object.keys(literals).forEach((k) => {
    literals[k] = applyTemplates(literals[k], pageContext);
  });
  const { props: bound, meta } = resolveBindings(node.bindings, datasetMap, pageContext);
  return { props: { ...literals, ...bound }, meta };
}
